import React, { useEffect, useState } from 'react';
import { Package, Plus, Trash2, Info, Save, Loader2, ChevronDown, X } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { moduleApi } from '../../lib/api';

type UnitType = 'unite' | 'm2' | 'surface';

interface PrestationCatalogItem {
    id: string;
    label: string;
    unit_price_ttc: number;
    tax_rate: number;
    show_tva: boolean;
    unit_type: UnitType;
    surface_width: number;
    surface_length: number;
    default_duration_minutes: number;
}

const UNIT_TYPE_LABELS: Record<UnitType, string> = {
    unite: 'À l\'unité',
    m2: 'Au m²',
    surface: 'Surface (ex: 200x300)',
};

const emptyItem = (defaultTva: number): PrestationCatalogItem => ({
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    label: '',
    unit_price_ttc: 0,
    tax_rate: defaultTva,
    show_tva: true,
    unit_type: 'unite',
    surface_width: 0,
    surface_length: 0,
    default_duration_minutes: 30,
});

function PrestationForm({
    item,
    allowedTvaRates,
    onChange,
}: {
    item: PrestationCatalogItem;
    allowedTvaRates: number[];
    onChange: (patch: Partial<PrestationCatalogItem>) => void;
}) {
    return (
        <div className="space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div className="space-y-1">
                    <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Prix TTC</label>
                    <input
                        type="number"
                        step="0.01"
                        min={0}
                        value={item.unit_price_ttc}
                        onChange={(e) => onChange({ unit_price_ttc: Math.max(0, Number(e.target.value || 0)) })}
                        className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2 text-sm text-content outline-none focus:border-brand-500 transition-colors"
                    />
                </div>
                <div className="space-y-1">
                    <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Type de tarif</label>
                    <select
                        value={item.unit_type}
                        onChange={(e) => onChange({ unit_type: e.target.value as UnitType })}
                        className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2 text-sm text-content outline-none focus:border-brand-500 transition-colors"
                    >
                        {(Object.keys(UNIT_TYPE_LABELS) as UnitType[]).map((key) => (
                            <option key={key} value={key}>{UNIT_TYPE_LABELS[key]}</option>
                        ))}
                    </select>
                </div>
                <div className="space-y-1">
                    <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Temps de travail par unité (min)</label>
                    <input
                        type="number"
                        step="5"
                        min={0}
                        value={item.default_duration_minutes}
                        onChange={(e) => onChange({ default_duration_minutes: Math.max(0, Number(e.target.value || 0)) })}
                        className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2 text-sm text-content outline-none focus:border-brand-500 transition-colors"
                    />
                </div>
            </div>
            <p className="text-[11px] text-content-muted italic">
                Ex: nettoyer 1 chaise = 10 min. Dans le Planning, la durée totale d'un rendez-vous se calculera automatiquement : quantité × ce temps ÷ nombre d'employés assignés.
            </p>

            {item.unit_type === 'surface' && (
                <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                        <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Largeur (ex: 200)</label>
                        <input
                            type="number"
                            step="1"
                            min={0}
                            value={item.surface_width}
                            onChange={(e) => onChange({ surface_width: Math.max(0, Number(e.target.value || 0)) })}
                            className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2 text-sm text-content outline-none focus:border-brand-500 transition-colors"
                        />
                    </div>
                    <div className="space-y-1">
                        <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Longueur (ex: 300)</label>
                        <input
                            type="number"
                            step="1"
                            min={0}
                            value={item.surface_length}
                            onChange={(e) => onChange({ surface_length: Math.max(0, Number(e.target.value || 0)) })}
                            className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2 text-sm text-content outline-none focus:border-brand-500 transition-colors"
                        />
                    </div>
                </div>
            )}

            <div className="flex items-center gap-4 flex-wrap">
                <label className="flex items-center gap-2 text-xs text-content-muted cursor-pointer">
                    <input
                        type="checkbox"
                        checked={item.show_tva}
                        onChange={(e) => onChange({ show_tva: e.target.checked })}
                        className="accent-brand-600"
                    />
                    Assujetti à la TVA
                </label>
                {item.show_tva && (
                    <div className="flex items-center gap-2">
                        <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">TVA</label>
                        <select
                            value={item.tax_rate}
                            onChange={(e) => onChange({ tax_rate: Number(e.target.value) })}
                            className="bg-surface-soft border border-border rounded-xl px-3 py-1.5 text-sm text-content outline-none focus:border-brand-500 transition-colors"
                        >
                            {allowedTvaRates.map((rate) => (
                                <option key={rate} value={rate}>{rate}%</option>
                            ))}
                        </select>
                    </div>
                )}
            </div>
        </div>
    );
}

function AddPrestationModal({
    allowedTvaRates,
    onClose,
    onCreate,
}: {
    allowedTvaRates: number[];
    onClose: () => void;
    onCreate: (item: PrestationCatalogItem) => void;
}) {
    const [draft, setDraft] = useState<PrestationCatalogItem>(emptyItem(allowedTvaRates[0] ?? 20));

    const handleCreate = () => {
        if (!draft.label.trim()) return;
        onCreate({ ...draft, label: draft.label.trim() });
    };

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="w-full max-w-lg bg-surface border border-border rounded-3xl max-h-[90vh] shadow-2xl flex flex-col overflow-hidden"
            >
                <div className="flex items-center justify-between p-6 border-b border-border shrink-0">
                    <h3 className="text-lg font-bold text-content flex items-center gap-2">
                        <Package size={18} className="text-brand-600 dark:text-brand-400" /> Nouvelle prestation
                    </h3>
                    <button onClick={onClose} className="p-2 text-content-muted hover:text-content rounded-xl hover:bg-surface-soft transition-all">
                        <X size={20} />
                    </button>
                </div>
                <div className="p-6 space-y-4 overflow-y-auto">
                    <div className="space-y-1">
                        <label className="text-xs font-bold text-content-muted uppercase tracking-widest">Nom de la prestation</label>
                        <input
                            type="text"
                            autoFocus
                            value={draft.label}
                            onChange={(e) => setDraft((d) => ({ ...d, label: e.target.value }))}
                            placeholder="Ex: Coupe homme, Nettoyage complet appartement..."
                            className="w-full bg-surface-soft border border-border rounded-xl px-4 py-3 text-sm text-content outline-none focus:border-brand-500 transition-colors"
                        />
                    </div>
                    <PrestationForm item={draft} allowedTvaRates={allowedTvaRates} onChange={(patch) => setDraft((d) => ({ ...d, ...patch }))} />
                </div>
                <div className="flex justify-end gap-3 p-6 border-t border-border shrink-0">
                    <button onClick={onClose} className="px-5 py-2.5 rounded-xl border border-border text-content-muted hover:text-content hover:bg-surface-soft font-bold text-sm transition-all">
                        Annuler
                    </button>
                    <button
                        onClick={handleCreate}
                        disabled={!draft.label.trim()}
                        className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg font-bold text-sm transition-all disabled:opacity-50"
                    >
                        <Plus size={15} /> Ajouter
                    </button>
                </div>
            </motion.div>
        </div>
    );
}

export const PrestationsModule: React.FC = () => {
    const [items, setItems] = useState<PrestationCatalogItem[]>([]);
    const [allowedTvaRates, setAllowedTvaRates] = useState<number[]>([20]);
    const [fullSettings, setFullSettings] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [expandedId, setExpandedId] = useState<string | null>(null);
    const [showAddModal, setShowAddModal] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const data = await moduleApi.getBillingSettings();
                setFullSettings(data || {});
                const rates = Array.isArray(data?.tva_rates) && data.tva_rates.length > 0 ? data.tva_rates.map(Number) : [20];
                setAllowedTvaRates(rates);
                let parsed: any[] = [];
                try {
                    parsed = JSON.parse(data?.prestations_catalog || '[]');
                } catch {
                    parsed = [];
                }
                setItems(
                    (Array.isArray(parsed) ? parsed : [])
                        .map((p: any, index: number) => ({
                            id: String(p?.id || index + 1),
                            label: String(p?.label || '').trim(),
                            unit_price_ttc: Number(p?.unit_price_ttc ?? 0),
                            tax_rate: Number(p?.tax_rate ?? rates[0] ?? 20),
                            show_tva: p?.show_tva !== false,
                            unit_type: (['unite', 'm2', 'surface'].includes(p?.unit_type) ? p.unit_type : 'unite') as UnitType,
                            surface_width: Number(p?.surface_width ?? 0),
                            surface_length: Number(p?.surface_length ?? 0),
                            default_duration_minutes: Number(p?.default_duration_minutes ?? 30),
                        }))
                        .filter((item: PrestationCatalogItem) => item.label.length > 0)
                );
            } catch (e) {
                console.error('Failed to load prestations catalog', e);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const updateItem = (id: string, patch: Partial<PrestationCatalogItem>) => {
        setItems((prev) => prev.map((item) => (item.id === id ? { ...item, ...patch } : item)));
    };

    const removeItem = (id: string) => {
        setItems((prev) => prev.filter((item) => item.id !== id));
        if (expandedId === id) setExpandedId(null);
    };

    const handleCreate = (item: PrestationCatalogItem) => {
        setItems((prev) => [...prev, item]);
        setShowAddModal(false);
        setExpandedId(item.id);
    };

    const handleSave = async () => {
        if (!fullSettings) return;
        setSaving(true);
        try {
            const prestations_catalog = JSON.stringify(
                items
                    .map((item, index) => ({
                        id: String(index + 1),
                        label: item.label.trim(),
                        unit_price_ttc: Number(item.unit_price_ttc ?? 0),
                        tax_rate: item.show_tva ? Number(item.tax_rate ?? allowedTvaRates[0] ?? 20) : 0,
                        show_tva: item.show_tva,
                        unit_type: item.unit_type,
                        surface_width: Number(item.surface_width ?? 0),
                        surface_length: Number(item.surface_length ?? 0),
                        default_duration_minutes: Number(item.default_duration_minutes ?? 30),
                    }))
                    .filter((item) => item.label.length > 0)
            );
            // On renvoie l'objet complet des paramètres de facturation (PUT remplace toute la ligne
            // en base) — seul le catalogue change, tout le reste (adresse, SIRET, etc.) est préservé.
            const { logo_url, default_tva_rate, default_tva_custom_rate, tva_rates, enable_cover_count, ...safeSettings } = fullSettings;
            await moduleApi.saveBillingSettings({ ...safeSettings, prestations_catalog });
            setSaved(true);
            setTimeout(() => setSaved(false), 2000);
        } catch (e) {
            console.error('Failed to save prestations catalog', e);
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="py-20 text-center text-content-muted flex flex-col items-center gap-2">
                <Loader2 size={28} className="animate-spin" />
                <span className="text-sm">Chargement du catalogue...</span>
            </div>
        );
    }

    return (
        <div className="space-y-8">
            <header className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-brand-500/15 flex items-center justify-center text-brand-500">
                        <Package size={22} />
                    </div>
                    <div>
                        <h1 className="text-2xl font-black text-content">Prestations</h1>
                        <p className="text-sm text-content-muted mt-1">Configurez les prestations que vous proposez : prix, TVA, durée estimée.</p>
                    </div>
                </div>
                <button
                    onClick={() => setShowAddModal(true)}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg text-sm font-bold shadow-lg shadow-brand-500/20 transition-all self-start md:self-auto"
                >
                    <Plus size={16} /> Ajouter une prestation
                </button>
            </header>

            <div className="flex gap-2.5 p-3.5 rounded-xl bg-brand-500/10 border border-brand-500/20">
                <Info size={16} className="text-brand-600 dark:text-brand-400 shrink-0 mt-0.5" />
                <p className="text-[11px] leading-relaxed text-content-muted">
                    Ce catalogue alimente automatiquement le module Facture et le module Planning (rendez-vous). Adaptez chaque prestation à votre métier (coiffure, nettoyage...) : type de tarif, TVA et temps estimé sont configurables individuellement. Les taux de TVA disponibles sont configurés par votre administrateur.
                </p>
            </div>

            <div className="space-y-2">
                {items.length === 0 ? (
                    <div className="text-center py-16 border border-dashed border-border rounded-2xl bg-surface/40">
                        <Package size={32} className="mx-auto mb-2 text-content-muted opacity-40" />
                        <p className="text-content-muted text-sm">Aucune prestation configurée pour l'instant.</p>
                    </div>
                ) : items.map((item) => {
                    const isExpanded = expandedId === item.id;
                    return (
                        <div key={item.id} className="rounded-2xl border border-border bg-surface overflow-hidden">
                            <button
                                type="button"
                                onClick={() => setExpandedId(isExpanded ? null : item.id)}
                                className="w-full flex items-center justify-between gap-3 px-4 py-3.5 text-left hover:bg-surface-soft/60 transition-colors"
                            >
                                <span className="font-bold text-content text-sm truncate">{item.label || 'Sans nom'}</span>
                                <div className="flex items-center gap-3 shrink-0">
                                    <span className="text-xs text-content-muted">{item.unit_price_ttc.toFixed(2)}€</span>
                                    <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); removeItem(item.id); }}
                                        className="p-1.5 rounded-lg text-red-400 hover:bg-red-500/10 transition-all"
                                        aria-label="Supprimer la prestation"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                    <ChevronDown size={16} className={`text-content-muted transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                </div>
                            </button>
                            <AnimatePresence>
                                {isExpanded && (
                                    <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: 'auto', opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        className="overflow-hidden"
                                    >
                                        <div className="px-4 pb-4 pt-1 border-t border-border space-y-3">
                                            <div className="space-y-1">
                                                <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Nom de la prestation</label>
                                                <input
                                                    type="text"
                                                    value={item.label}
                                                    onChange={(e) => updateItem(item.id, { label: e.target.value })}
                                                    className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2 text-sm text-content outline-none focus:border-brand-500 transition-colors"
                                                />
                                            </div>
                                            <PrestationForm item={item} allowedTvaRates={allowedTvaRates} onChange={(patch) => updateItem(item.id, patch)} />
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    );
                })}
            </div>

            <div className="flex justify-end">
                <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center gap-2 px-6 py-3 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg font-bold text-sm transition-all disabled:opacity-50"
                >
                    <Save size={16} /> {saving ? 'Enregistrement...' : saved ? 'Enregistré ✓' : 'Enregistrer'}
                </button>
            </div>

            {showAddModal && (
                <AddPrestationModal
                    allowedTvaRates={allowedTvaRates}
                    onClose={() => setShowAddModal(false)}
                    onCreate={handleCreate}
                />
            )}
        </div>
    );
};
